import React, { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";
import { 
  fetchPowerBIEmbedConfig, 
  refreshPowerBIEmbedToken,
  setCurrentDashboard,
  resetDashboardCache 
} from "../../../store/powerbi/actions";

const PowerBIViewer = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { 
    embedConfig, 
    embedConfigs, 
    loadedDashboards, 
    currentKeyword, 
    loading, 
    error 
  } = useSelector((state) => state.PowerBI);
  
  const reportRef = useRef(null);
  const intervalRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Extract keyword from pathname
  const getKeywordFromPath = useCallback(() => {
    const path = location.pathname;
    if (path.includes('/dashboard/')) {
      return path.replace("-", "").split("/").pop();
    }
    return null;
  }, [location.pathname]);

  const getTitleFromPath = useCallback(() => {
    const path = location.pathname;
    if (path.includes('/dashboard/')) {
      const segment = path.split("/").pop();
      // Split by hyphens, capitalize each word, and join with spaces
      return segment
        .split("-")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    }
    return null;
  }, [location.pathname]);

  // Handle screen size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Main effect: Handle route changes and dashboard loading
  useEffect(() => {
    const keywordFromPath = getKeywordFromPath();

    if (!keywordFromPath) return;

    // If the keyword changed, set it as current
    if (keywordFromPath !== currentKeyword) {
      dispatch(setCurrentDashboard(keywordFromPath));
    }

    // Check if dashboard is already loaded
    const isDashboardLoaded = loadedDashboards.has(keywordFromPath);
    
    // Only fetch if:
    // 1. Dashboard is not already loaded
    // 2. We're not currently loading
    // 3. There's no error for this specific dashboard
    if (!isDashboardLoaded && !loading) {
      // console.log(`Fetching PowerBI config for: ${keywordFromPath}`);
      dispatch(fetchPowerBIEmbedConfig(keywordFromPath));
    } else if (isDashboardLoaded) {
      // console.log(`Dashboard ${keywordFromPath} already loaded, using cached version`);
    }
  }, [location.pathname, currentKeyword, loadedDashboards, loading, dispatch, getKeywordFromPath]);

  // Handle token refresh - separate from main loading logic
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Only set up refresh if we have a valid embed token
    if (embedConfig?.embedToken) {
      intervalRef.current = setInterval(() => {
        dispatch(refreshPowerBIEmbedToken());
      }, 50 * 60 * 1000); // 50 minutes
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [embedConfig?.embedToken, dispatch]);

  // Update access token in embedded report when token changes
  useEffect(() => {
    if (embedConfig?.embedToken && reportRef.current) {
      reportRef.current.setAccessToken(embedConfig.embedToken);
    }
  }, [embedConfig?.embedToken]);

  const pathKeyword = getKeywordFromPath();
  const pathTitle = getTitleFromPath();

  // Don't render if not on a dashboard route
  if (!pathKeyword) {
    return <div>Please navigate to a dashboard route</div>;
  }

  // Show loading only when actively loading the current dashboard
  if (loading && currentKeyword === pathKeyword) {
    return <div>Loading Power BI Report for {pathTitle}...</div>;
  }
  
  if (error) {
    return (
      <div>
        Error loading {pathTitle} Dashboard: {error}
        <button 
          onClick={() => {
            dispatch(resetDashboardCache(pathKeyword));
            dispatch(fetchPowerBIEmbedConfig(pathKeyword));
          }}
          className="ml-2 px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }
  
  // Check if we have embedConfig for current dashboard
  const currentEmbedConfig = embedConfigs[pathKeyword] || embedConfig;
  
  if (!currentEmbedConfig) {
    return <div>No dashboard configuration available for {pathTitle}</div>;
  }

  return (
    <div className="w-full h-full">
      {/* Debug info - remove in production */}
      {/* <div className="text-xs text-gray-500 p-2">
        Dashboard: {pathTitle} | 
        Loaded: {loadedDashboards.has(pathKeyword) ? 'Yes' : 'No'} | 
        Loading: {loading ? 'Yes' : 'No'}
      </div> */}
      
      <PowerBIEmbed
        embedConfig={{
          type: "report",
          id: currentEmbedConfig.reportId,
          embedUrl: currentEmbedConfig.embedUrl,
          accessToken: currentEmbedConfig.embedToken,
          tokenType: models.TokenType.Embed,
          settings: {
            panes: { 
              filters: { visible: !isMobile }, 
              pageNavigation: { visible: !isMobile } 
            },
            layoutType: isMobile ? models.LayoutType.MobilePortrait : models.LayoutType.Master,
            customLayout: {
              displayOption: models.DisplayOption.FitToPage
            }
          },
        }}
        eventHandlers={
          new Map([
            ["loaded", () => console.log(`Report loaded for ${pathTitle} Dashboard`)],
            ["error", (event) => console.error(`Power BI Error for ${pathTitle} Dashboard:`, event.detail)],
          ])
        }
        cssClassName="w-full h-full"
        getEmbeddedComponent={(embeddedReport) => {
          reportRef.current = embeddedReport;
        }}
      />
    </div>
  );
};

export default PowerBIViewer;